import { useHistory } from 'react-router-dom';
import type { MutationResult, FetchResult } from '@apollo/client';

import {
  CommunityCreateInput,
  useCreateCommunityMutation,
  useUpdateCommunityMutation,
} from 'generated/graphql';
import { showMessageAction } from 'containers/Notification/actions';

export const useCommunityMutation = (
  communityId: string,
  entityType: 'team' | 'community' = 'community'
): [(data: CommunityCreateInput) => void, MutationResult] => {
  const history = useHistory();
  const entityTypePlural = entityType === 'team' ? 'teams' : 'communities';

  const onCompleted: Exclude<
    Parameters<typeof useCreateCommunityMutation>[0],
    undefined
  >['onCompleted'] = (e) => {
    showMessageAction(
      communityId
        ? `${entityType === 'team' ? 'Team' : 'Community'} updated`
        : `${entityType === 'team' ? 'Team' : 'Community'} created`
    );
    const effectiveCommunityId = e?.createCommunity?.id || communityId;
    history.push(
      effectiveCommunityId ? `/${entityTypePlural}/${effectiveCommunityId}` : `/${entityTypePlural}`
    );
  };

  const [mutationCreate, resultCreate] = useCreateCommunityMutation({
    onCompleted,
    update: (cache) => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'communities' });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'communitiesCount' });
    },
  });

  const [mutationUpdate, resultUpdate] = useUpdateCommunityMutation({
    onCompleted,
    update: (cache) => {
      if (cache?.modify) {
        cache.modify({
          id: cache.identify({ __typename: 'Community', id: communityId }),
          fields: {
            members() {
              return undefined;
            },
          },
        });
      }
    },
  });

  const mutation = (data: CommunityCreateInput): Promise<FetchResult> =>
    communityId
      ? mutationUpdate({
          variables: {
            data,
            communityId,
          },
        })
      : mutationCreate({ variables: { data } });

  const result = communityId ? resultUpdate : resultCreate;

  return [mutation, result];
};
